import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, TrendingDown, Users, Target, PieChart, BarChart3,
  Loader2, RefreshCw, DollarSign, Percent, AlertTriangle,
  CheckCircle2, ArrowUpRight, ArrowDownRight, Package, Plus, Trash2, Link
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant KPI Card
function KPICard({ title, value, subtitle, icon, color, trend, trendValue }) {
  var Icon = icon;
  var TrendIcon = trend === 'up' ? ArrowUpRight : ArrowDownRight;
  var trendColor = trend === 'up' ? 'text-emerald-500' : 'text-red-500';
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={'p-2 rounded-lg ' + color}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{title}</p>
              <p className="text-xl font-bold text-slate-900">{value}</p>
              {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
            </div>
          </div>
          {trendValue && (
            <div className={'flex items-center gap-1 ' + trendColor}>
              <TrendIcon className="h-4 w-4" />
              <span className="text-sm font-medium">{trendValue}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Composant Client Row
function ClientRow({ client, formatMontant, isTop }) {
  var margeColor = client.taux_marge >= 30 ? 'text-emerald-600' : client.taux_marge >= 10 ? 'text-amber-600' : 'text-red-600';
  var margeBg = client.taux_marge >= 30 ? 'bg-emerald-100' : client.taux_marge >= 10 ? 'bg-amber-100' : 'bg-red-100';
  
  return (
    <tr className={'border-b hover:bg-slate-50 ' + (isTop ? 'bg-emerald-50/30' : '')}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          {isTop && <span className="text-amber-500">⭐</span>}
          <span className="font-medium">{client.client}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-right font-mono text-blue-600">{formatMontant(client.ca)}</td>
      <td className="py-3 px-4 text-right font-mono text-slate-600">{client.nb_transactions}</td>
      <td className="py-3 px-4 text-right font-mono text-red-600">{formatMontant(client.cout_total)}</td>
      <td className="py-3 px-4 text-right font-mono text-emerald-600">{formatMontant(client.marge_nette)}</td>
      <td className="py-3 px-4 text-right">
        <span className={'px-2 py-1 rounded-full text-sm font-medium ' + margeBg + ' ' + margeColor}>
          {client.taux_marge}%
        </span>
      </td>
    </tr>
  );
}

// Composant Prestation Row
function PrestationRow({ prestation, formatMontant, maxCa }) {
  var progressWidth = maxCa > 0 ? (prestation.ca / maxCa) * 100 : 0;
  
  return (
    <div className="p-4 border rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">{prestation.nature}</span>
        <span className="text-blue-600 font-bold">{formatMontant(prestation.ca)}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
        <div 
          className="h-full bg-blue-500 rounded-full transition-all"
          style={{ width: progressWidth + '%' }}
        />
      </div>
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{prestation.nb_transactions} transactions</span>
        <span>{prestation.pourcentage_ca}% du CA</span>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-500 mt-1">
        <span>Moyenne: {formatMontant(prestation.montant_moyen)}</span>
        <span>Min: {formatMontant(prestation.montant_min)} / Max: {formatMontant(prestation.montant_max)}</span>
      </div>
    </div>
  );
}

function RentabilitePage() {
  const { token } = useAuth();
  const { formatMontant } = useSettings();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState(null);
  const [prestations, setPrestations] = useState(null);
  const [seuil, setSeuil] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [allocations, setAllocations] = useState(null);
  const [depensesNonAllouees, setDepensesNonAllouees] = useState(null);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [selectedDepense, setSelectedDepense] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  var fetchDashboard = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/dashboard', {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token]);

  var fetchClients = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/clients', {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [token]);

  var fetchPrestations = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/prestations', {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setPrestations(response.data);
    } catch (error) {
      console.error('Error fetching prestations:', error);
    }
  }, [token]);

  var fetchSeuil = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/seuil', {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setSeuil(response.data);
    } catch (error) {
      console.error('Error fetching seuil:', error);
    }
  }, [token]);

  var fetchCatalogue = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/catalogue', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setCatalogue(response.data);
    } catch (error) {
      console.error('Error fetching catalogue:', error);
    }
  }, [token]);

  var fetchAllocations = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/allocations', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setAllocations(response.data);
    } catch (error) {
      console.error('Error fetching allocations:', error);
    }
  }, [token]);

  var fetchDepensesNonAllouees = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/rentabilite/depenses-non-allouees', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setDepensesNonAllouees(response.data);
    } catch (error) {
      console.error('Error fetching depenses:', error);
    }
  }, [token]);

  useEffect(function() {
    async function loadAll() {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchClients(), fetchPrestations(), fetchSeuil(), fetchCatalogue(), fetchAllocations(), fetchDepensesNonAllouees()]);
      setLoading(false);
    }
    loadAll();
  }, [fetchDashboard, fetchClients, fetchPrestations, fetchSeuil, fetchCatalogue, fetchAllocations, fetchDepensesNonAllouees]);

  function refreshData() {
    fetchDashboard();
    fetchClients();
    fetchPrestations();
    fetchSeuil();
    fetchCatalogue();
    fetchAllocations();
    fetchDepensesNonAllouees();
    toast.success('Données actualisées');
  }

  // Allocation functions
  var [allocationForm, setAllocationForm] = useState({ client: '', pourcentage: 100 });
  var [savingAllocation, setSavingAllocation] = useState(false);

  async function handleCreateAllocation() {
    if (!selectedDepense || !allocationForm.client) {
      toast.error('Sélectionnez un client');
      return;
    }
    
    setSavingAllocation(true);
    try {
      await axios.post(API_URL + '/api/rentabilite/allocation', {
        depense_id: selectedDepense.id,
        client: allocationForm.client,
        pourcentage: parseFloat(allocationForm.pourcentage) || 100
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      toast.success('Allocation créée');
      setShowAllocationModal(false);
      setSelectedDepense(null);
      setAllocationForm({ client: '', pourcentage: 100 });
      fetchAllocations();
      fetchDepensesNonAllouees();
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSavingAllocation(false);
    }
  }

  async function handleDeleteAllocation(allocationId) {
    try {
      await axios.delete(API_URL + '/api/rentabilite/allocation/' + allocationId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Allocation supprimée');
      fetchAllocations();
      fetchDepensesNonAllouees();
      fetchClients();
    } catch (error) {
      toast.error('Erreur');
    }
  }

  function openAllocationModal(depense) {
    setSelectedDepense(depense);
    setAllocationForm({ client: '', pourcentage: depense.pourcentage_restant || 100 });
    setShowAllocationModal(true);
  }

  // Liste des clients uniques pour le select (extraction des noms)
  var clientNamesForSelect = clients?.clients?.map(function(c) { return c.client; }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Extract data safely
  var kpis = dashboard ? dashboard.kpis : {};
  var topClients = dashboard ? dashboard.top_clients : [];
  var evolution = dashboard ? dashboard.evolution_mensuelle : [];
  var clientsList = clients ? clients.clients : [];
  var prestationsList = prestations ? prestations.prestations : [];
  var seuilData = seuil || {};
  var seuilRentabilite = seuilData.seuil_rentabilite || {};
  var charges = seuilData.charges || {};
  var margeSecurite = seuilData.marge_securite || {};

  // Build client rows
  var clientRows = [];
  for (var i = 0; i < clientsList.length; i++) {
    clientRows.push(
      <ClientRow 
        key={i} 
        client={clientsList[i]} 
        formatMontant={formatMontant}
        isTop={i < 3}
      />
    );
  }

  // Build prestation cards
  var maxCa = prestationsList.length > 0 ? prestationsList[0].ca : 0;
  var prestationCards = [];
  for (var j = 0; j < prestationsList.length; j++) {
    prestationCards.push(
      <PrestationRow 
        key={j} 
        prestation={prestationsList[j]} 
        formatMontant={formatMontant}
        maxCa={maxCa}
      />
    );
  }

  // Build evolution bars
  var maxEvolution = 0;
  for (var k = 0; k < evolution.length; k++) {
    if (evolution[k].ca > maxEvolution) maxEvolution = evolution[k].ca;
  }

  var evolutionBars = [];
  var moisNoms = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  for (var m = 0; m < evolution.length; m++) {
    var ev = evolution[m];
    var height = maxEvolution > 0 ? (ev.ca / maxEvolution) * 100 : 0;
    evolutionBars.push(
      <div key={m} className="flex flex-col items-center">
        <div className="h-32 w-8 bg-slate-100 rounded-t relative flex items-end">
          <div 
            className="w-full bg-blue-500 rounded-t transition-all"
            style={{ height: height + '%' }}
          />
        </div>
        <span className="text-xs text-slate-500 mt-1">{moisNoms[m]}</span>
        <span className="text-xs font-mono text-slate-700">{ev.ca > 0 ? formatMontant(ev.ca) : '-'}</span>
      </div>
    );
  }

  // Top clients cards
  var topClientCards = [];
  for (var t = 0; t < topClients.length; t++) {
    var tc = topClients[t];
    topClientCards.push(
      <div key={t} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-amber-500 font-bold">{t + 1}</span>
          <span className="font-medium">{tc.client}</span>
        </div>
        <span className="text-blue-600 font-bold">{formatMontant(tc.ca)}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rentabilite-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rentabilité & Coût de Revient</h1>
          <p className="text-slate-500 mt-1">Analysez la rentabilité de votre activité par client et prestation</p>
        </div>
        <Button onClick={refreshData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Vue d&apos;ensemble
          </TabsTrigger>
          <TabsTrigger value="clients">
            <Users className="h-4 w-4 mr-2" />
            Par client
          </TabsTrigger>
          <TabsTrigger value="prestations">
            <PieChart className="h-4 w-4 mr-2" />
            Par prestation
          </TabsTrigger>
          <TabsTrigger value="catalogue">
            <Package className="h-4 w-4 mr-2" />
            Catalogue
          </TabsTrigger>
          <TabsTrigger value="allocations">
            <ArrowUpRight className="h-4 w-4 mr-2" />
            Allocations
          </TabsTrigger>
          <TabsTrigger value="seuil">
            <Target className="h-4 w-4 mr-2" />
            Seuil de rentabilité
          </TabsTrigger>
        </TabsList>

        {/* TAB: Dashboard */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Chiffre d'affaires"
              value={formatMontant(kpis.ca_total || 0)}
              icon={DollarSign}
              color="bg-blue-100 text-blue-600"
            />
            <KPICard
              title="Résultat"
              value={formatMontant(kpis.resultat || 0)}
              subtitle={kpis.resultat >= 0 ? 'Bénéfice' : 'Perte'}
              icon={kpis.resultat >= 0 ? TrendingUp : TrendingDown}
              color={kpis.resultat >= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}
            />
            <KPICard
              title="Taux de marge"
              value={kpis.taux_marge + '%'}
              icon={Percent}
              color="bg-purple-100 text-purple-600"
            />
            <KPICard
              title="Nombre de clients"
              value={kpis.nb_clients || 0}
              subtitle={'CA moyen: ' + formatMontant(kpis.ca_moyen_client || 0)}
              icon={Users}
              color="bg-amber-100 text-amber-600"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clients */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-amber-500" />
                  Top 3 Clients
                </CardTitle>
                <CardDescription>Vos meilleurs clients par chiffre d&apos;affaires</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {topClientCards.length > 0 ? topClientCards : (
                  <p className="text-center text-slate-500 py-4">Aucun client pour le moment</p>
                )}
              </CardContent>
            </Card>

            {/* Evolution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  Évolution mensuelle
                </CardTitle>
                <CardDescription>CA par mois en 2026</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between gap-1">
                  {evolutionBars}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB: Clients */}
        <TabsContent value="clients" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Rentabilité par client</CardTitle>
              <CardDescription>
                Analysez la marge générée par chaque client (⭐ = Top 3)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clientsList.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Client</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">CA</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Transactions</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Coûts</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Marge nette</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Taux</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientRows}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune donnée client disponible</p>
                  <p className="text-sm mt-2">Ajoutez des recettes pour voir l&apos;analyse par client</p>
                </div>
              )}
            </CardContent>
          </Card>

          {clients && clients.depenses_non_allouees > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="p-4 flex items-center gap-4">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Dépenses non allouées</p>
                  <p className="text-amber-700 text-sm">
                    {formatMontant(clients.depenses_non_allouees)} de dépenses n&apos;ont pas été attribuées à un client. 
                    Elles sont réparties proportionnellement au CA.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Prestations */}
        <TabsContent value="prestations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Répartition par type de prestation</CardTitle>
              <CardDescription>Analysez vos revenus par nature de prestation</CardDescription>
            </CardHeader>
            <CardContent>
              {prestationsList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {prestationCards}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <PieChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune prestation enregistrée</p>
                  <p className="text-sm mt-2">Ajoutez des recettes avec une nature pour voir la répartition</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Catalogue */}
        <TabsContent value="catalogue" className="space-y-6">
          {catalogue ? (
            <>
              {/* KPIs du catalogue */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="CA du Catalogue"
                  value={formatMontant(catalogue.kpis?.ca_total || 0)}
                  subtitle={catalogue.kpis?.nb_prestations + ' prestations'}
                  icon={DollarSign}
                  color="bg-blue-100 text-blue-600"
                />
                <KPICard
                  title="Marge Totale"
                  value={formatMontant(catalogue.kpis?.marge_totale || 0)}
                  subtitle={'Taux: ' + (catalogue.kpis?.taux_marge_global || 0) + '%'}
                  icon={TrendingUp}
                  color="bg-emerald-100 text-emerald-600"
                />
                <KPICard
                  title="Prestations Rentables"
                  value={catalogue.kpis?.nb_rentables || 0}
                  subtitle="Marge ≥ 20%"
                  icon={CheckCircle2}
                  color="bg-green-100 text-green-600"
                />
                <KPICard
                  title="À Optimiser"
                  value={catalogue.kpis?.nb_a_optimiser || 0}
                  subtitle="Marge < 20%"
                  icon={AlertTriangle}
                  color="bg-amber-100 text-amber-600"
                />
              </div>

              {/* Liste des prestations avec rentabilité */}
              <Card>
                <CardHeader>
                  <CardTitle>Rentabilité par Prestation</CardTitle>
                  <CardDescription>Basé sur les ventes réelles depuis vos factures</CardDescription>
                </CardHeader>
                <CardContent>
                  {catalogue.prestations && catalogue.prestations.length > 0 ? (
                    <div className="space-y-3">
                      {catalogue.prestations.filter(p => p.ca_reel > 0).map(function(p) {
                        var margeColor = p.taux_marge_reel >= 30 ? 'text-emerald-600' : p.taux_marge_reel >= 20 ? 'text-amber-600' : 'text-red-600';
                        var margeBg = p.taux_marge_reel >= 30 ? 'bg-emerald-100' : p.taux_marge_reel >= 20 ? 'bg-amber-100' : 'bg-red-100';
                        return (
                          <div key={p.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{p.nom}</p>
                              <p className="text-xs text-muted-foreground">
                                {p.quantite_vendue} vendus · Prix catalogue: {formatMontant(p.prix_catalogue)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{formatMontant(p.ca_reel)}</p>
                              <p className="text-xs text-muted-foreground">CA réel</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-emerald-600">{formatMontant(p.marge_brute)}</p>
                              <p className="text-xs text-muted-foreground">Marge</p>
                            </div>
                            <div className={'px-3 py-1 rounded-full text-sm font-medium ' + margeBg + ' ' + margeColor}>
                              {p.taux_marge_reel}%
                            </div>
                          </div>
                        );
                      })}
                      
                      {catalogue.prestations.filter(p => p.ca_reel > 0).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
                          <p>Aucune prestation vendue pour le moment</p>
                          <p className="text-sm">Créez des factures pour voir vos stats de rentabilité</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>Aucune prestation dans votre catalogue</p>
                      <a href="/app/catalogue" className="text-blue-600 hover:underline text-sm">
                        Ajouter des prestations
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Prestations non vendues */}
              {catalogue.non_vendues && catalogue.non_vendues.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Prestations Non Vendues</CardTitle>
                    <CardDescription>Ces prestations n&apos;ont pas encore été facturées</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {catalogue.non_vendues.map(function(p) {
                        return (
                          <span key={p.id} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                            {p.nom}
                          </span>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p>Chargement...</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Allocations */}
        <TabsContent value="allocations" className="space-y-6">
          {/* Info card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Link className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Allocation des dépenses</p>
                  <p className="text-sm text-blue-700">
                    Associez vos dépenses à des clients pour calculer la marge réelle par client.
                    Les dépenses non allouées sont réparties proportionnellement au CA.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dépenses non allouées */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Dépenses à allouer</span>
                  {depensesNonAllouees && (
                    <span className="text-sm font-normal text-muted-foreground">
                      {formatMontant(depensesNonAllouees.montant_total || 0)} non alloués
                    </span>
                  )}
                </CardTitle>
                <CardDescription>Associez ces dépenses à un client</CardDescription>
              </CardHeader>
              <CardContent>
                {depensesNonAllouees && depensesNonAllouees.depenses?.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {depensesNonAllouees.depenses.map(function(dep) {
                      return (
                        <div key={dep.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{dep.nature}</p>
                            <p className="text-xs text-muted-foreground">
                              {dep.fournisseur || 'Fournisseur inconnu'} · {dep.date}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="font-semibold text-sm">{formatMontant(dep.montant)}</p>
                              {dep.pourcentage_alloue > 0 && (
                                <p className="text-xs text-amber-600">{dep.pourcentage_restant}% restant</p>
                              )}
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={function() { openAllocationModal(dep); }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Allouer
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                    <p className="font-medium">Toutes les dépenses sont allouées !</p>
                    <p className="text-sm">Vos marges clients sont calculées précisément.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Allocations existantes */}
            <Card>
              <CardHeader>
                <CardTitle>Allocations existantes</CardTitle>
                <CardDescription>Dépenses associées à des clients</CardDescription>
              </CardHeader>
              <CardContent>
                {allocations && allocations.allocations?.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {allocations.allocations.map(function(alloc) {
                      return (
                        <div key={alloc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{alloc.depense?.nature || 'Dépense'}</p>
                            <p className="text-xs text-muted-foreground">
                              → {alloc.client} ({alloc.pourcentage}%)
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm">{formatMontant(alloc.montant_alloue)}</p>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100"
                              onClick={function() { handleDeleteAllocation(alloc.id); }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Link className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Aucune allocation</p>
                    <p className="text-sm">Allouez des dépenses pour affiner vos marges</p>
                  </div>
                )}

                {/* Résumé par client */}
                {allocations && allocations.totaux_par_client && Object.keys(allocations.totaux_par_client).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Totaux par client</p>
                    <div className="space-y-1">
                      {Object.entries(allocations.totaux_par_client).map(function([client, total]) {
                        return (
                          <div key={client} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{client}</span>
                            <span className="font-medium">{formatMontant(total)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB: Seuil de rentabilité */}
        <TabsContent value="seuil" className="space-y-6">
          {/* Status */}
          <Card className={seuilData.rentable ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}>
            <CardContent className="p-4 flex items-center gap-4">
              {seuilData.rentable ? (
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-red-600" />
              )}
              <div>
                <p className={'font-semibold ' + (seuilData.rentable ? 'text-emerald-800' : 'text-red-800')}>
                  {seuilData.rentable ? 'Activité rentable' : 'Seuil de rentabilité non atteint'}
                </p>
                <p className={(seuilData.rentable ? 'text-emerald-700' : 'text-red-700') + ' text-sm'}>
                  {seuilData.rentable 
                    ? 'Votre CA dépasse votre point mort de ' + formatMontant(seuilRentabilite.ecart || 0)
                    : 'Il vous manque ' + formatMontant(Math.abs(seuilRentabilite.ecart || 0)) + ' pour atteindre le seuil'
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Seuil de rentabilité */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Point mort
                </CardTitle>
                <CardDescription>CA minimum pour couvrir vos charges</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-6 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Seuil de rentabilité annuel</p>
                  <p className="text-3xl font-bold text-blue-600">{formatMontant(seuilRentabilite.annuel || 0)}</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Soit {formatMontant(seuilRentabilite.mensuel || 0)} / mois
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progression vers le seuil</span>
                    <span className="font-medium">{seuilRentabilite.pourcentage_atteint || 0}%</span>
                  </div>
                  <Progress value={Math.min(seuilRentabilite.pourcentage_atteint || 0, 100)} />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-xs text-blue-600">CA actuel</p>
                    <p className="text-lg font-bold text-blue-700">{formatMontant(seuilData.ca_total || 0)}</p>
                  </div>
                  <div className={'p-3 rounded-lg text-center ' + (seuilData.rentable ? 'bg-emerald-50' : 'bg-red-50')}>
                    <p className={'text-xs ' + (seuilData.rentable ? 'text-emerald-600' : 'text-red-600')}>
                      {seuilData.rentable ? 'Excédent' : 'Manque'}
                    </p>
                    <p className={'text-lg font-bold ' + (seuilData.rentable ? 'text-emerald-700' : 'text-red-700')}>
                      {formatMontant(Math.abs(seuilRentabilite.ecart || 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Structure des charges */}
            <Card>
              <CardHeader>
                <CardTitle>Structure des charges</CardTitle>
                <CardDescription>Répartition entre charges fixes et variables</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Charges fixes</p>
                    <p className="text-xs text-slate-500">Loyer, assurances, abonnements...</p>
                  </div>
                  <p className="text-lg font-bold text-slate-700">{formatMontant(charges.fixes || 0)}</p>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Charges variables</p>
                    <p className="text-xs text-slate-500">Proportionnelles à l&apos;activité</p>
                  </div>
                  <p className="text-lg font-bold text-slate-700">{formatMontant(charges.variables || 0)}</p>
                </div>

                <div className="border-t pt-3">
                  <div className="flex items-center justify-between">
                    <p className="font-bold">Total des charges</p>
                    <p className="text-xl font-bold text-red-600">{formatMontant(charges.total || 0)}</p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-800">Marge de sécurité</p>
                  <p className="text-2xl font-bold text-purple-600">{formatMontant(margeSecurite.montant || 0)}</p>
                  <p className="text-sm text-purple-600">{margeSecurite.taux || 0}% du CA</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal d'allocation */}
      <Dialog open={showAllocationModal} onOpenChange={setShowAllocationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Allouer une dépense</DialogTitle>
            <DialogDescription>
              Associez cette dépense à un client pour calculer sa marge réelle
            </DialogDescription>
          </DialogHeader>
          
          {selectedDepense && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedDepense.nature}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedDepense.fournisseur} · {selectedDepense.date}
                </p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  {formatMontant(selectedDepense.montant)}
                </p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>Client</Label>
                  <Select 
                    value={allocationForm.client} 
                    onValueChange={function(val) { setAllocationForm({...allocationForm, client: val}); }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientNamesForSelect.map(function(clientName) {
                        return (
                          <SelectItem key={clientName} value={clientName}>
                            {clientName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Pourcentage à allouer</Label>
                  <Input 
                    type="number"
                    min="1"
                    max={selectedDepense.pourcentage_restant || 100}
                    value={allocationForm.pourcentage}
                    onChange={function(e) { setAllocationForm({...allocationForm, pourcentage: e.target.value}); }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Maximum: {selectedDepense.pourcentage_restant || 100}%
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowAllocationModal(false); }}>
              Annuler
            </Button>
            <Button onClick={handleCreateAllocation} disabled={savingAllocation}>
              {savingAllocation ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Allouer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RentabilitePage;
